import React, { type ChangeEvent, useState, type FC, memo, forwardRef, type RefObject } from 'react';
import { type GeneralFieldProps } from '../../../types/Field';
import { captureIfThrows } from '../../../utils/errorReporter';
import { formatCardExpiry } from '../../CardForm/utils/formatting';
import { sanitizeCardExpiry } from '../../CardForm/utils/sanitizeUtils';
import { maxExpireInputSize } from './constants';
import { CardExpireDateView } from './view';

type CardExpireDateControllerProps = GeneralFieldProps;

export const CardExpireDateController: FC<CardExpireDateControllerProps> = memo(
  forwardRef((props: CardExpireDateControllerProps, ref: RefObject<HTMLInputElement>) => {
    const { value, onChange } = props;
    const formattedValue: string = formatCardExpiry(value);
    // ChangeEvent.inputType isn't supported in IE11, thus tracking manually
    const [lastKey, setLastKey] = useState<string>();

    const handleChange = captureIfThrows((e: ChangeEvent<HTMLInputElement>) => {
      // prev value -React process-> event value & caret -calc-> next value and caret
      const inputElement: HTMLInputElement = e.currentTarget;
      const prevFormattedValue: string = formattedValue;
      const eventFormattedValue: string = e.target.value;
      const eventFinalCaretPos: number = inputElement.selectionStart;
      let nextFormattedValue: string = formatCardExpiry(eventFormattedValue);
      let nextCaretPos: number = eventFinalCaretPos;
      let allowAutoBlurIfValid = true;

      // char added
      if (eventFormattedValue.length - prevFormattedValue.length === 1) {
        allowAutoBlurIfValid = false;
        if (prevFormattedValue.length === maxExpireInputSize) {
          // ignore any change if already max length
          nextFormattedValue = prevFormattedValue;
        }

        nextFormattedValue = formatCardExpiry(eventFormattedValue);

        if (nextFormattedValue === prevFormattedValue) {
          // React automatically increments caret pos, even if we'll ignore the change
          // thus need to counter-decrement
          nextCaretPos -= 1;
        } else if (eventFinalCaretPos === eventFormattedValue.length) {
          nextCaretPos = nextFormattedValue.length;
          allowAutoBlurIfValid = true;
        } else if (
          eventFormattedValue.charAt(eventFinalCaretPos) === ' ' &&
          eventFinalCaretPos === eventFormattedValue.length - 1
        ) {
          nextCaretPos += 1;
          allowAutoBlurIfValid = true;
        } else if (
          nextFormattedValue[nextFormattedValue.length - 1] !== prevFormattedValue[prevFormattedValue.length - 1]
        ) {
          nextCaretPos = nextFormattedValue.length;
        }
      } else if (
        prevFormattedValue.length - eventFormattedValue.length === 1 &&
        ['Backspace', 'Delete'].includes(lastKey) &&
        // part of separator was removed
        ((prevFormattedValue[eventFinalCaretPos] === ' ' && eventFormattedValue[eventFinalCaretPos] !== ' ') ||
          (prevFormattedValue[eventFinalCaretPos] === '/' && eventFormattedValue[eventFinalCaretPos] !== '/'))
      ) {
        allowAutoBlurIfValid = false;
        let extraDigitToRemoveI: number;
        let modifiedEventFormattedValue: string = eventFormattedValue;

        if (lastKey === 'Backspace') {
          extraDigitToRemoveI = eventFormattedValue.slice(0, eventFinalCaretPos).match(/(\d)\D*$/)?.index;

          const sanitizedCardExpiry = sanitizeCardExpiry(eventFormattedValue);
          const [, year] = sanitizedCardExpiry.split('/');

          if (!year) {
            if (sanitizedCardExpiry.length > 2 && sanitizedCardExpiry[sanitizedCardExpiry.length - 1] !== '/') {
              // When year exist, but separator doesn't and digit is supposed to be deleted inside the expiry date
              modifiedEventFormattedValue = sanitizedCardExpiry;
            } else {
              // remove whole separator if year doesn't exist
              modifiedEventFormattedValue = modifiedEventFormattedValue.slice(0, extraDigitToRemoveI + 1);
            }
          }
        } else {
          extraDigitToRemoveI = eventFormattedValue.slice(eventFinalCaretPos).match(/^\D*(\d)/)?.index;
        }

        if (Number.isInteger(extraDigitToRemoveI)) {
          nextFormattedValue = formatCardExpiry(
            modifiedEventFormattedValue
              .split('')
              .filter((char, i) => i !== extraDigitToRemoveI)
              .join(''),
          );

          if (lastKey === 'Backspace') {
            nextCaretPos = extraDigitToRemoveI;
          }
        }
      }

      onChange(
        nextFormattedValue,
        captureIfThrows(() => {
          if (inputElement.setSelectionRange && document.activeElement === inputElement) {
            inputElement.setSelectionRange(nextCaretPos, nextCaretPos);
          }
        }),
        allowAutoBlurIfValid,
      );
    });

    return (
      <CardExpireDateView
        {...props}
        ref={ref}
        onKeyDown={(e) => {
          setLastKey(e.key);
        }}
        onChange={handleChange}
      />
    );
  }),
);
CardExpireDateController.displayName = 'CardExpireDateController';
